/**
 * NotificationCentre — dropdown panel opened by NotificationBell.
 *
 * Implements:
 * - AC36: Tab filtering (All / Tickets / Learning / System)
 * - AC49: "N new notifications" banner on SSE arrivals
 * - AC64: Infinite scroll via IntersectionObserver on last item
 * - AC35: Mark All Read button, Preferences button
 * - Accessibility: role="dialog", aria-labelledby, Escape to close
 * - Responsive: 400px desktop, full-screen on mobile (<768px)
 */

import React, { useEffect, useRef, useCallback, useState } from 'react';
import { X, CheckCheck, Settings, RefreshCw } from 'lucide-react';
import { useNotifications } from './NotificationContext.js';
import { NotificationItem } from './NotificationItem.js';
import type { NotificationCategory } from '../../api/notifications.js';

interface NotificationCentreProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type TabId = 'all' | 'tickets' | 'learning' | 'system';

interface Tab {
  id: TabId;
  label: string;
  categories: NotificationCategory[];
}

const TABS: Tab[] = [
  { id: 'all', label: 'All', categories: [] },
  {
    id: 'tickets',
    label: 'Tickets',
    categories: ['ticket_update', 'ticket_assignment', 'incident_alert', 'incident_resolved'],
  },
  {
    id: 'learning',
    label: 'Learning',
    categories: ['learning_reminder', 'learning_completion'],
  },
  {
    id: 'system',
    label: 'System',
    categories: ['system', 'admin', 'product_update', 'onboarding'],
  },
];

interface NotificationCentreProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onOpenPreferences?: () => void;
}

export function NotificationCentre({
  open,
  onOpenChange,
  onOpenPreferences,
}: NotificationCentreProps) {
  const {
    notifications,
    totalUnread,
    total,
    isLoading,
    error,
    newArrivals,
    clearNewArrivals,
    markAsRead,
    markAllAsRead,
    archive,
    loadMore,
    refresh,
  } = useNotifications();

  const [activeTab, setActiveTab] = useState<TabId>('all');
  const panelRef = useRef<HTMLDivElement>(null);
  const headingId = useRef(`notif-centre-${Math.random().toString(36).slice(2)}`).current;

  // ── Keyboard: Escape closes the panel ────────────────────────────────────

  useEffect(() => {
    if (!open) return;

    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onOpenChange(false);
      }
    }

    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onOpenChange]);

  // ── Focus first element on open ───────────────────────────────────────────

  useEffect(() => {
    if (open && panelRef.current) {
      const firstFocusable = panelRef.current.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      firstFocusable?.focus();
    }
  }, [open]);

  // ── IntersectionObserver for infinite scroll (AC64) ──────────────────────

  const observerRef = useRef<IntersectionObserver | null>(null);
  const lastItemRef = useCallback(
    (node: HTMLElement | null) => {
      if (isLoading) return;
      if (observerRef.current) observerRef.current.disconnect();
      if (!node) return;

      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0]?.isIntersecting && notifications.length < total) {
          void loadMore();
        }
      });
      observerRef.current.observe(node);
    },
    [isLoading, notifications.length, total, loadMore],
  );

  // ── Filter notifications by active tab ───────────────────────────────────

  const activeTabDef = TABS.find((t) => t.id === activeTab)!;
  const visibleNotifications =
    activeTab === 'all'
      ? notifications
      : notifications.filter((n) => activeTabDef.categories.includes(n.category));

  // ── Unread count for active tab ───────────────────────────────────────────

  const tabUnread =
    activeTab === 'all'
      ? totalUnread
      : visibleNotifications.filter((n) => !n.read).length;

  const handleMarkAllRead = () => {
    const category =
      activeTab === 'all' ? undefined : (activeTabDef.categories[0] as NotificationCategory);
    void markAllAsRead(activeTab === 'all' ? undefined : undefined);
  };

  const handleNewArrivals = () => {
    clearNewArrivals();
    void refresh();
  };

  if (!open) return null;

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-labelledby={headingId}
      aria-modal="true"
      className={`
        absolute right-0 z-50 flex flex-col bg-white shadow-xl border border-gray-200
        focus:outline-none
        /* desktop */
        top-full mt-2 w-[400px] max-h-[600px] rounded-xl
        /* mobile: full screen via media query overrides below */
      `}
      style={{ maxHeight: 'min(600px, calc(100vh - 80px))' }}
    >
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-4 pt-4 pb-2 flex-shrink-0">
        <h2 id={headingId} className="text-base font-semibold text-gray-900">
          Notifications
        </h2>
        <div className="flex items-center gap-1">
          {tabUnread > 0 && (
            <button
              type="button"
              onClick={handleMarkAllRead}
              title="Mark all as read"
              aria-label="Mark all as read"
              className="
                p-1.5 rounded-lg text-gray-500 hover:text-green-600 hover:bg-green-50
                focus:outline-none focus:ring-1 focus:ring-green-500 transition-colors
                text-xs flex items-center gap-1
              "
            >
              <CheckCheck className="w-4 h-4" />
              <span className="hidden sm:inline">Mark all read</span>
            </button>
          )}
          {onOpenPreferences && (
            <button
              type="button"
              onClick={onOpenPreferences}
              title="Notification preferences"
              aria-label="Notification preferences"
              className="
                p-1.5 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100
                focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors
              "
            >
              <Settings className="w-4 h-4" />
            </button>
          )}
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            title="Close"
            aria-label="Close notifications"
            className="
              p-1.5 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100
              focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors
            "
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* ── Tab bar ──────────────────────────────────────────────────────── */}
      <div
        role="tablist"
        aria-label="Notification categories"
        className="flex gap-0 px-4 border-b border-gray-100 flex-shrink-0"
      >
        {TABS.map((tab) => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={activeTab === tab.id}
            aria-controls={`notif-panel-${tab.id}`}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`
              px-3 py-2 text-sm font-medium border-b-2 transition-colors
              focus:outline-none focus:ring-1 focus:ring-inset focus:ring-blue-500
              ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── New arrivals banner (AC49) ────────────────────────────────────── */}
      {newArrivals > 0 && (
        <button
          type="button"
          onClick={handleNewArrivals}
          className="
            mx-4 mt-2 flex items-center justify-center gap-2
            py-1.5 px-3 rounded-lg bg-blue-50 text-blue-700 text-xs font-medium
            hover:bg-blue-100 transition-colors
            focus:outline-none focus:ring-1 focus:ring-blue-500 flex-shrink-0
          "
        >
          <RefreshCw className="w-3.5 h-3.5" />
          {newArrivals === 1
            ? '1 new notification — click to refresh'
            : `${newArrivals} new notifications — click to refresh`}
        </button>
      )}

      {/* ── Notification list ─────────────────────────────────────────────── */}
      <div
        id={`notif-panel-${activeTab}`}
        role="tabpanel"
        aria-label={`${TABS.find((t) => t.id === activeTab)?.label} notifications`}
        className="flex-1 overflow-y-auto overscroll-contain"
      >
        {isLoading && visibleNotifications.length === 0 && (
          <div className="flex items-center justify-center py-12 text-sm text-gray-400">
            <RefreshCw className="w-4 h-4 animate-spin mr-2" />
            Loading…
          </div>
        )}

        {!isLoading && error && (
          <div className="flex flex-col items-center justify-center py-12 gap-2">
            <p className="text-sm text-red-500">{error}</p>
            <button
              type="button"
              onClick={() => void refresh()}
              className="text-xs text-blue-600 hover:underline focus:outline-none"
            >
              Retry
            </button>
          </div>
        )}

        {!isLoading && !error && visibleNotifications.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 gap-2">
            <p className="text-sm text-gray-400">
              {activeTab === 'all' ? 'No notifications yet' : `No ${activeTab} notifications`}
            </p>
          </div>
        )}

        {visibleNotifications.map((notification, i) => {
          const isLast = i === visibleNotifications.length - 1;
          return (
            <React.Fragment key={notification.id}>
              <NotificationItem
                notification={notification}
                onMarkRead={markAsRead}
                onArchive={archive}
                lastItemRef={isLast ? lastItemRef : undefined}
              />
              {i < visibleNotifications.length - 1 && (
                <hr className="border-gray-100" />
              )}
            </React.Fragment>
          );
        })}

        {/* Load more spinner */}
        {isLoading && visibleNotifications.length > 0 && (
          <div className="flex items-center justify-center py-4 text-sm text-gray-400">
            <RefreshCw className="w-3.5 h-3.5 animate-spin mr-2" />
            Loading more…
          </div>
        )}

        {/* All loaded message */}
        {!isLoading && notifications.length >= total && total > 0 && (
          <p className="text-center text-xs text-gray-400 py-4">
            All {total} notification{total === 1 ? '' : 's'} loaded
          </p>
        )}
      </div>
    </div>
  );
}
